import type { KeyboardEvent, ReactNode } from "react";
import * as Abas from "@radix-ui/react-tabs";
import { AlertTriangle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

/*
 * Abas de entidade, controladas pelo CAMINHO (design D2).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ CADA ABA É UM LINK DE VERDADE, COM ENTRADA NO HISTÓRICO.                 │
 * │                                                                          │
 * │ Debaixo de uma aba de entidade abre outra entidade, e o voltar do        │
 * │ navegador precisa passar por ela. Ctrl+clique abre em aba nova, e F5     │
 * │ reabre no mesmo lugar. Aba de LISTA é outra coisa: fica em `?aba=`, com  │
 * │ `replace`, como em `Faturas`.                                            │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Radix `Tabs` cuida do papel `tab` e das setas. `activationMode="manual"` porque a
 * seta só move o foco: ativar na seta encheria o histórico de abas por onde o teclado
 * passou. Quem navega é o `Link`.
 *
 * O conteúdo é `children`, normalmente `<Outlet />`, e continua montado: a aba de
 * contas segue embaixo do painel lateral, com a mesma rolagem.
 */
export interface AbaDaRota {
  /** O segmento do caminho, depois de `base`. */
  para: string;
  rotulo: string;
  /** Pendências bloqueantes da aba. Zero ou ausente não mostra marca. */
  marca?: number | undefined;
  /**
   * Um aviso que não bloqueia: o fato em poucas palavras, com o triângulo âmbar no gatilho,
   * para quem está em outra aba ver sem adivinhar qual abrir. O nome da aba não muda, e o
   * aviso por extenso fica no conteúdo dela.
   */
  aviso?: string | undefined;
  /** Outros segmentos em que a aba fica ativa: `convenios` é o painel sobre Contas. */
  tambemEm?: readonly string[];
}

function ativarComEspaco(evento: KeyboardEvent<HTMLAnchorElement>) {
  // Enter o link já resolve. Espaço é do papel `tab`, e num `<a>` ele rolaria a tela.
  if (evento.key !== " ") return;
  evento.preventDefault();
  evento.currentTarget.click();
}

export function AbasDaRota({
  base,
  rotulo,
  abas,
  children,
}: {
  /** O caminho da entidade, sem barra no fim: `/cedentes/{id}`. */
  base: string;
  /** Nome acessível da lista de abas. */
  rotulo: string;
  abas: readonly AbaDaRota[];
  children: ReactNode;
}) {
  const local = useLocation();
  const estado: unknown = local.state;
  const segmento = local.pathname.slice(base.length + 1).split("/")[0] ?? "";
  const ativa =
    abas.find((aba) => aba.para === segmento || aba.tambemEm?.includes(segmento) === true)?.para ??
    "";

  return (
    <Abas.Root value={ativa} activationMode="manual">
      <Abas.List aria-label={rotulo} className="flex gap-1 border-b border-fio">
        {abas.map((aba) => (
          <Abas.Trigger key={aba.para} value={aba.para} asChild>
            <Link
              to={`${base}/${aba.para}`}
              // O "« voltar" continua sabendo de que lista o operador veio.
              state={estado}
              onKeyDown={ativarComEspaco}
              className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-3 py-2 text-corpo text-texto-2 hover:text-texto data-[state=active]:border-esferografica data-[state=active]:text-texto"
            >
              {aba.rotulo}
              {aba.marca !== undefined && aba.marca > 0 && (
                <>
                  {/* Ícone e texto, nunca só cor (doc 06a §7): o texto é do leitor de tela. */}
                  <AlertTriangle aria-hidden="true" className="size-3.5 text-falha" />
                  <span className="sr-only">
                    , {aba.marca === 1 ? "1 pendência" : `${aba.marca} pendências`}
                  </span>
                </>
              )}
              {aba.aviso !== undefined && (
                <span aria-hidden="true" title={aba.aviso} className="text-transito">
                  <AlertTriangle className="size-3.5" />
                </span>
              )}
            </Link>
          </Abas.Trigger>
        ))}
      </Abas.List>

      <Abas.Content value={ativa} className="pt-4 focus:outline-none">
        {children}
      </Abas.Content>
    </Abas.Root>
  );
}
